import { getFormattedMonthYear } from './dateUtils'

/**
 * Attributo ALT delle immagini dei veicoli.
 *
 * Template richiesto per la foto principale di ogni offerta:
 *   {Brand} {Modello} {Versione} {Anno} – vista laterale sinistra
 *
 * Il template si applica quando lo scatto e' davvero una laterale sinistra.
 * Quando non lo e', l'alt dichiara l'angolazione reale ricavata dall'alt che
 * arriva da offers-explorer: descrivere un'inquadratura diversa da quella
 * mostrata vale meno di un alt generico, sia per chi usa uno screen reader sia
 * per la SEO. Sulle offerte di noleggio, ad esempio, la prima foto e' quasi
 * sempre un frontale e non una laterale sinistra.
 */

export const MAIN_IMAGE_VIEW = 'vista laterale sinistra'

/** Prima foto con angolazione non ricavabile: e' comunque un esterno dell'auto. */
export const UNKNOWN_MAIN_VIEW = 'vista esterna'

/** Foto successive alla prima con angolazione non ricavabile. */
export const UNKNOWN_VIEW = 'altra vista'

const ALT_SEPARATOR = ' – '

export interface VehicleAltData {
  name?: string
  brand?: string
  model?: string
  version?: string
  year?: string | number
}

/**
 * Angolazioni riconosciute, dalla piu' specifica alla piu' generica:
 * l'ordine conta perche' "Anteriore Sinistra" deve vincere su "Anteriore".
 * Le stringhe che non descrivono un'inquadratura ("Vista Identificativa",
 * "Offerta auto {Modello}") non hanno una regola di proposito: devono cadere
 * sul fallback invece di far dichiarare un'angolazione inventata.
 */
const VIEW_RULES: Array<{ pattern: RegExp; view: string }> = [
  { pattern: /(anteriore|frontale|front)[\s-]*(sinistr\w*|sx)/i, view: 'vista tre quarti anteriore sinistra' },
  { pattern: /(anteriore|frontale|front)[\s-]*(destr\w*|dx)/i, view: 'vista tre quarti anteriore destra' },
  { pattern: /(posteriore|retro|rear)[\s-]*(sinistr\w*|sx)/i, view: 'vista tre quarti posteriore sinistra' },
  { pattern: /(posteriore|retro|rear)[\s-]*(destr\w*|dx)/i, view: 'vista tre quarti posteriore destra' },
  { pattern: /(laterale|fiancata|lato|side)[\s-]*(sinistr\w*|sx)/i, view: 'vista laterale sinistra' },
  { pattern: /(laterale|fiancata|lato|side)[\s-]*(destr\w*|dx)/i, view: 'vista laterale destra' },
  { pattern: /(obliqu\w*|tre quarti|3\/4)/i, view: 'vista tre quarti' },
  { pattern: /(interni|abitacolo|sedili)/i, view: 'vista interni' },
  { pattern: /(plancia|cruscotto|dashboard)/i, view: 'vista plancia e cruscotto' },
  { pattern: /(bagagliaio|baule|trunk)/i, view: 'vista bagagliaio' },
  { pattern: /(cerchi|cerchio|ruote|wheel)/i, view: 'dettaglio cerchi' },
  { pattern: /(anteriore|frontale|front)/i, view: 'vista frontale' },
  { pattern: /(posteriore|retro|rear)/i, view: 'vista posteriore' },
  { pattern: /(laterale|fiancata|side)/i, view: 'vista laterale' },
]

const squash = (value: string) => value.replace(/\s+/g, ' ').trim()

/**
 * "{Brand} {Modello} {Versione} {Anno}".
 * Le offerte di noleggio riguardano auto nuove e non espongono un anno di
 * immatricolazione, quindi si usa l'anno corrente, coerente con title e meta
 * description. Le offerte di vendita che valorizzano `year` usano il proprio.
 */
export const getVehicleAltBase = ({ name, brand, model, version, year }: VehicleAltData = {}) => {
  const { formattedYear } = getFormattedMonthYear()
  const vehicleName = name || `${brand || ''} ${model || ''}`

  return squash([vehicleName, version, `${year || formattedYear}`].filter(Boolean).join(' '))
}

/**
 * Ricava l'angolazione dall'alt di origine, null se non e' riconoscibile.
 */
export const getVehicleImageView = (sourceAlt?: string): string | null => {
  if (!sourceAlt) {
    return null
  }

  const rule = VIEW_RULES.find(({ pattern }) => pattern.test(sourceAlt))

  return rule ? rule.view : null
}

/**
 * Alt completo di una foto veicolo. `view` e' obbligatorio: va passata
 * l'angolazione reale dello scatto, non un default.
 */
export const getVehicleImageAlt = (vehicle: VehicleAltData, view: string) =>
  `${getVehicleAltBase(vehicle)}${ALT_SEPARATOR}${view}`

/**
 * Alt per le card delle liste, dove l'angolazione spesso non e' nota: viene
 * aggiunta solo se ricavabile dall'alt di origine, altrimenti si resta sul
 * veicolo piu' il contesto (es. "noleggio a lungo termine").
 */
export const getOfferCardAlt = (vehicle: VehicleAltData, context: string, sourceAlt?: string) => {
  const base = squash(`${getVehicleAltBase(vehicle)} ${context}`)
  const view = getVehicleImageView(sourceAlt)

  return view ? `${base}${ALT_SEPARATOR}${view}` : base
}

/**
 * Alt dei loghi di marca, unico punto in cui si compone la stringa "Logo ...".
 *
 * Va usata ovunque compaia un logo di marca - card marchi, titolo del PDP,
 * banner partnership, heading della privacy - cosi' la forma della stringa
 * resta una sola. Il `context` opzionale aggiunge la qualifica quando serve
 * distinguere il contesto (es. "Noleggio a Lungo Termine").
 */
export const getBrandLogoAlt = (brand: string, context?: string) =>
  squash(`Logo ${brand || ''} ${context || ''}`)

/**
 * Dati anagrafici di un'offerta di vendita nella forma attesa dagli helper.
 * Il dettaglio espone brand/model/version sia sulla radice sia su `vehicle`,
 * che pero' e' opzionale e viene popolato solo se l'enrichment riesce.
 */
export const getSellVehicleAltData = (offer: OfferGlobal.SellOfferDetail): VehicleAltData => ({
  name: offer.vehicle?.name,
  brand: offer.vehicle?.brand || offer.brand,
  model: offer.vehicle?.model || offer.model,
  version: offer.vehicle?.version || offer.version,
  year: offer.year,
})

/**
 * Riscrive gli alt di tutte le foto della gallery mantenendo l'angolazione
 * reale dello scatto. Eventuali collisioni vengono numerate per non avere due
 * alt identici nella stessa pagina.
 */
export const getGalleryImagesAlt = (
  images: Array<OfferGlobal.CarImageItem>,
  vehicle: VehicleAltData,
): Array<OfferGlobal.CarImageItem> => {
  if (!images || !images.length) {
    return []
  }

  const usedAlts: Array<string> = []

  return images.map((image, index) => {
    const view = getVehicleImageView(image.alt) || (index === 0 ? UNKNOWN_MAIN_VIEW : UNKNOWN_VIEW)

    const baseAlt = getVehicleImageAlt(vehicle, view)
    let alt = baseAlt
    let occurrence = 1

    while (usedAlts.indexOf(alt) !== -1) {
      occurrence += 1
      alt = `${baseAlt} (${occurrence})`
    }

    usedAlts.push(alt)

    return { ...image, alt }
  })
}
